import { defaults } from 'lodash';
import React from 'react';
import Currency from '../../../currency';
import { useUser } from '../../../user_hooks';
import type { FormattedString, TagType } from '../../types';
import { RCTextInput } from '../RCTextInput';

interface Props {
  /** ID of the input component, must be unique across the page */
  id: string;

  /** html attr, used by html form submission */
  name: string;

  /** Main label text (or more complex components if you really need to) */
  label: FormattedString;

  disabled?: boolean;

  /** Signals to browser's form validator that a non-empty value must be selected */
  required?: boolean;

  /** Validation error message */
  errorText?: string;

  /** Additional info displayed below label */
  helpText?: FormattedString;

  /** Annotation on the field, e.g. 'required', 'optional' */
  tag?: TagType;

  /** called when value changes; updated value in evt.target.value */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  /** called when input element loses focus */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;

  /** Current value of the input in major units */
  value: string;

  /** Currency for value */
  currencyCode: string;

  /** Text that appears when the value is empty */
  placeholder?: string;

  /** Static text displayed inline after the Currency display code. Useful for "plus shipping" or other context */
  suffixText?: string;
}

const NUMERIC_PATTERN = '[0-9.,\\s]*';

/**
 * RCTextInput for money input
 */
export function RCMoneyInput(props: Props) {
  const { symbol } = defaults(Currency.get(props.currencyCode), { symbol: '' });
  const { locale, countryCode } = useUser();

  function suffixText() {
    if (locale === 'en' && countryCode === 'US' && props.currencyCode === 'USD') {
      return props.suffixText;
    }

    return [props.currencyCode, props.suffixText].join(' ').trim();
  }

  return (
    <RCTextInput
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      prefixText={symbol}
      suffixText={suffixText()}
      type="text"
      pattern={NUMERIC_PATTERN}
    />
  );
}
